// src/pages/MovementAnalysis.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import TableHeader from "../components/TableHeader";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";
import { readStoredFinancialYearStartYear } from "../utils/financialYear";
import { getMovementReport } from "../services/movementAnalysisService";

function useDebouncedValue(value, delay = 450) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const toNumberSafe = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v.replace(/,/g, "").trim());
    return Number.isNaN(n) ? 0 : n;
  }
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
};

function formatMonthLabel(year, monthIndex) {
  return new Date(year, monthIndex, 1).toLocaleString("default", {
    month: "short",
    year: "numeric",
  });
}

function buildFyMonthKeys() {
  const fyStartYear = readStoredFinancialYearStartYear();
  const months = [];
  let cursor = new Date(fyStartYear, 3, 1);
  const end = new Date(fyStartYear + 1, 2, 31);
  while (cursor <= end) {
    const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
    months.push({
      key,
      month: formatMonthLabel(cursor.getFullYear(), cursor.getMonth()),
    });
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }
  return months;
}

export default function MovementAnalysisReport() {
  const [report, setReport] = useState([]);
  const [monthlyByPart, setMonthlyByPart] = useState({});
  const [ledgerByKey, setLedgerByKey] = useState({});
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ field: null, direction: "asc" });
  const [viewMode, setViewMode] = useState("report");
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedAbstract, setSelectedAbstract] = useState(null);
  const [showChart, setShowChart] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [importRowCount, setImportRowCount] = useState(0);

  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const [fromDate, setFromDate] = useState(monthStart.toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(monthEnd.toISOString().slice(0, 10));
  const debouncedFrom = useDebouncedValue(fromDate);
  const debouncedTo = useDebouncedValue(toDate);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getMovementReport(debouncedFrom || null, debouncedTo || null);
        if (!mounted) return;
        setReport(data.report || []);
        setMonthlyByPart(data.monthlyByPart || {});
        setLedgerByKey(data.ledgerByKey || {});
        setImportRowCount(data.rowCount || 0);
        setSelectedItem(null);
        setSelectedAbstract(null);
        setViewMode("report");
      } catch (e) {
        console.error("MovementAnalysis load failed:", e);
        if (!mounted) return;
        setError(e?.message || "Failed to load movement data from uploaded sales import.");
        setReport([]);
        setMonthlyByPart({});
        setLedgerByKey({});
        setImportRowCount(0);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [debouncedFrom, debouncedTo]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key !== "Escape") return;
      if (showChart) setShowChart(false);
      else if (viewMode === "ledger") {
        setViewMode("abstract");
        setSelectedAbstract(null);
      } else if (viewMode === "abstract") {
        setViewMode("report");
        setSelectedItem(null);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [viewMode, showChart]);

  const handleSort = useCallback((field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  const handleFilter = useCallback((field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value.toString().toLowerCase() }));
  }, []);

  const filteredData = useMemo(
    () =>
      report.filter((row) =>
        Object.keys(filters).every(
          (field) =>
            !filters[field] ||
            (row[field] || "").toString().toLowerCase().includes(filters[field])
        )
      ),
    [report, filters]
  );

  const sortedData = useMemo(() => {
    const list = [...filteredData];
    if (!sortConfig.field) return list;
    list.sort((a, b) => {
      const valA = a[sortConfig.field];
      const valB = b[sortConfig.field];
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [filteredData, sortConfig]);

  const abstractData = useMemo(() => {
    if (!selectedItem) return [];
    const partMonths = monthlyByPart[selectedItem.code] || {};
    return buildFyMonthKeys().map(({ key, month }) => {
      const m = partMonths[key];
      return m
        ? { ...m, month }
        : { key, month, inward: 0, outward: 0, value: 0 };
    });
  }, [selectedItem, monthlyByPart]);

  const ledgerData = useMemo(() => {
    if (!selectedItem || !selectedAbstract) return [];
    const key = `${selectedItem.code}|${selectedAbstract.key}`;
    return ledgerByKey[key] || [];
  }, [selectedItem, selectedAbstract, ledgerByKey]);

  const getTotals = useCallback((data) => {
    return data.reduce(
      (acc, row) => {
        acc.inward += toNumberSafe(row.inward);
        acc.outward += toNumberSafe(row.outward);
        acc.value += toNumberSafe(row.value);
        acc.qty += toNumberSafe(row.qty);
        acc.openingQty += toNumberSafe(row.openingQty);
        acc.inwardQty += toNumberSafe(row.inwardQty);
        acc.outwardQty += toNumberSafe(row.outwardQty);
        acc.creditSalesQty += toNumberSafe(row.creditSalesQty);
        acc.closingQty += toNumberSafe(row.closingQty);
        acc.closingValue += toNumberSafe(row.closingValue);
        return acc;
      },
      {
        openingQty: 0,
        inwardQty: 0,
        outwardQty: 0,
        creditSalesQty: 0,
        closingQty: 0,
        closingValue: 0,
        inward: 0,
        outward: 0,
        value: 0,
        qty: 0,
      }
    );
  }, []);

  const totalsReport = useMemo(() => getTotals(sortedData), [sortedData, getTotals]);
  const totalsAbstract = useMemo(() => getTotals(abstractData), [abstractData, getTotals]);
  const totalsLedger = useMemo(() => getTotals(ledgerData), [ledgerData, getTotals]);

  const openAbstract = (row) => {
    setSelectedItem(row);
    setViewMode("abstract");
  };

  const openLedger = (monthRow) => {
    setSelectedAbstract(monthRow);
    setViewMode("ledger");
  };

  return (
    <div className="min-h-screen bg-[#f1f2f4] flex flex-col">
      <MenuBar />
      <HeaderBar />

      <div className="bg-[#dedfe3] border-x border-b border-gray-400 px-2 py-2 text-xs flex gap-2 items-center flex-wrap">
        <Link to="/stock-report" className="px-3 py-1 bg-zinc-200 hover:bg-zinc-300">
          Stock Status
        </Link>
        <Link to="/monthly-product-report" className="px-3 py-1 bg-zinc-200 hover:bg-zinc-300">
          Product Analysis
        </Link>
        <button type="button" className="px-3 py-1 bg-zinc-800 text-white">
          Movement Analysis (VIEW)
        </button>

        <div className="ml-2 flex items-center gap-2">
          <label className="text-xs">From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="text-xs px-2 py-1 border rounded"
          />
          <label className="text-xs">To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="text-xs px-2 py-1 border rounded"
          />
          <button
            type="button"
            onClick={() => {
              setFromDate(monthStart.toISOString().slice(0, 10));
              setToDate(monthEnd.toISOString().slice(0, 10));
            }}
            className="text-xs px-2 py-1 border rounded"
          >
            This month
          </button>
        </div>

        {loading && <span className="text-gray-600 ml-2">Loading…</span>}
        {!loading && importRowCount > 0 && (
          <span className="text-gray-600 ml-2">{importRowCount.toLocaleString()} import lines</span>
        )}
      </div>

      {error && (
        <div className="mx-2 mt-2 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded">
          {error}
        </div>
      )}

      {viewMode === "report" && (
        <div className="flex-1 overflow-hidden p-2">
          <div className="max-h-[70vh] overflow-auto border border-gray-400 bg-white">
            <table className="min-w-max border-collapse text-xs w-full">
              <thead className="bg-gray-300 sticky top-0 z-30">
                <tr>
                  <TableHeader label="Group" field="group" onSort={handleSort} onFilter={handleFilter} />
                  <TableHeader label="Brand" field="brand" onSort={handleSort} onFilter={handleFilter} />
                  <TableHeader label="Item" field="name" onSort={handleSort} onFilter={handleFilter} />
                  <TableHeader label="Part No" field="code" onSort={handleSort} onFilter={handleFilter} />
                  <TableHeader label="Opening Qty" field="openingQty" onSort={handleSort} />
                  <TableHeader label="Inward Qty" field="inwardQty" onSort={handleSort} />
                  <TableHeader label="Outward Qty" field="outwardQty" onSort={handleSort} />
                  <TableHeader label="Credit Sales Qty" field="creditSalesQty" onSort={handleSort} />
                  <TableHeader label="Closing Qty" field="closingQty" onSort={handleSort} />
                  <TableHeader label="Closing Value" field="closingValue" onSort={handleSort} />
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row) => (
                  <tr
                    key={row.code}
                    tabIndex={0}
                    onClick={() => openAbstract(row)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") openAbstract(row);
                    }}
                    className="hover:bg-blue-50 cursor-pointer focus:bg-blue-100 odd:bg-white even:bg-gray-50"
                  >
                    <td className="border px-2">{row.group}</td>
                    <td className="border px-2">{row.brand}</td>
                    <td className="border px-2">{row.name}</td>
                    <td className="border px-2">{row.code}</td>
                    <td className="border px-2 text-right">{toNumberSafe(row.openingQty).toFixed(2)}</td>
                    <td className="border px-2 text-right">{toNumberSafe(row.inwardQty).toFixed(2)}</td>
                    <td className="border px-2 text-right">{toNumberSafe(row.outwardQty).toFixed(2)}</td>
                    <td className="border px-2 text-right">{toNumberSafe(row.creditSalesQty).toFixed(2)}</td>
                    <td className="border px-2 text-right">{toNumberSafe(row.closingQty).toFixed(2)}</td>
                    <td className="border px-2 text-right">{toNumberSafe(row.closingValue).toFixed(2)}</td>
                  </tr>
                ))}
                {sortedData.length === 0 && !loading && (
                  <tr>
                    <td colSpan={10} className="border px-4 py-6 text-center text-gray-500">
                      No movement data for the selected period. Upload sales data first.
                    </td>
                  </tr>
                )}
                {sortedData.length > 0 && (
                  <tr className="font-semibold bg-gray-200 sticky bottom-0">
                    <td colSpan={4} className="border px-2">
                      TOTAL ({sortedData.length} items)
                    </td>
                    <td className="border px-2 text-right">{totalsReport.openingQty.toFixed(2)}</td>
                    <td className="border px-2 text-right">{totalsReport.inwardQty.toFixed(2)}</td>
                    <td className="border px-2 text-right">{totalsReport.outwardQty.toFixed(2)}</td>
                    <td className="border px-2 text-right">{totalsReport.creditSalesQty.toFixed(2)}</td>
                    <td className="border px-2 text-right">{totalsReport.closingQty.toFixed(2)}</td>
                    <td className="border px-2 text-right">{totalsReport.closingValue.toFixed(2)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-600 mt-2">
            Click a row for monthly abstract. Opening qty is net movement from FY start through the day before
            &quot;From&quot; date.
          </p>
        </div>
      )}

      {viewMode === "abstract" && selectedItem && (
        <div className="p-4">
          <button
            type="button"
            onClick={() => {
              setViewMode("report");
              setSelectedItem(null);
            }}
            className="mb-2 text-xs px-2 py-1 border rounded hover:bg-gray-100"
          >
            Back to report
          </button>
          <h3 className="font-semibold mb-2 text-sm">
            Monthly abstract — {selectedItem.name} ({selectedItem.code})
          </h3>
          <button
            type="button"
            onClick={() => setShowChart(true)}
            className="mb-4 px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
          >
            View chart
          </button>
          <div className="max-h-[65vh] overflow-auto border border-gray-400 bg-white">
            <table className="w-full max-w-2xl border-collapse text-xs">
              <thead className="bg-gray-200 sticky top-0">
                <tr>
                  <th className="border px-2 text-left">Month</th>
                  <th className="border px-2 text-right">Inward</th>
                  <th className="border px-2 text-right">Outward</th>
                  <th className="border px-2 text-right">Value</th>
                </tr>
              </thead>
              <tbody>
                {abstractData.map((m) => (
                  <tr
                    key={m.key}
                    tabIndex={0}
                    onClick={() => openLedger(m)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") openLedger(m);
                    }}
                    className="hover:bg-blue-50 cursor-pointer odd:bg-white even:bg-gray-50"
                  >
                    <td className="border px-2">{m.month}</td>
                    <td className="border px-2 text-right">{toNumberSafe(m.inward).toFixed(2)}</td>
                    <td className="border px-2 text-right">{toNumberSafe(m.outward).toFixed(2)}</td>
                    <td className="border px-2 text-right">{toNumberSafe(m.value).toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="font-semibold bg-gray-200">
                  <td className="border px-2">TOTAL</td>
                  <td className="border px-2 text-right">{totalsAbstract.inward.toFixed(2)}</td>
                  <td className="border px-2 text-right">{totalsAbstract.outward.toFixed(2)}</td>
                  <td className="border px-2 text-right">{totalsAbstract.value.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {viewMode === "ledger" && selectedItem && selectedAbstract && (
        <div className="p-4">
          <button
            type="button"
            onClick={() => {
              setViewMode("abstract");
              setSelectedAbstract(null);
            }}
            className="mb-2 text-xs px-2 py-1 border rounded hover:bg-gray-100"
          >
            Back to abstract
          </button>
          <h3 className="font-semibold mb-2 text-sm">
            Ledger — {selectedItem.name} ({selectedAbstract.month})
          </h3>
          <div className="max-h-[65vh] overflow-auto border border-gray-400 bg-white">
            <table className="w-full border-collapse text-xs">
              <thead className="bg-gray-200 sticky top-0">
                <tr>
                  <th className="border px-2 text-left">Date</th>
                  <th className="border px-2 text-left">Voucher No</th>
                  <th className="border px-2 text-left">Party</th>
                  <th className="border px-2 text-left">Type</th>
                  <th className="border px-2 text-right">Qty</th>
                  <th className="border px-2 text-right">Value</th>
                </tr>
              </thead>
              <tbody>
                {ledgerData.length === 0 && (
                  <tr>
                    <td colSpan={6} className="border px-4 py-4 text-center text-gray-500">
                      No vouchers for this month
                    </td>
                  </tr>
                )}
                {ledgerData.map((l, i) => (
                  <tr key={`${l.voucherNo}-${i}`} className="odd:bg-white even:bg-gray-50">
                    <td className="border px-2">{l.dateYmd || "-"}</td>
                    <td className="border px-2">{l.voucherNo}</td>
                    <td className="border px-2">{l.party}</td>
                    <td className="border px-2">{l.type}</td>
                    <td className="border px-2 text-right">{toNumberSafe(l.qty).toFixed(2)}</td>
                    <td className="border px-2 text-right">{toNumberSafe(l.value).toFixed(2)}</td>
                  </tr>
                ))}
                {ledgerData.length > 0 && (
                  <tr className="font-semibold bg-gray-200">
                    <td colSpan={4} className="border px-2">
                      TOTAL
                    </td>
                    <td className="border px-2 text-right">{totalsLedger.qty.toFixed(2)}</td>
                    <td className="border px-2 text-right">{totalsLedger.value.toFixed(2)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AnimatePresence>
        {showChart && selectedItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowChart(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-lg shadow-lg p-4 w-full max-w-4xl max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-sm">Chart — {selectedItem.name}</h3>
                <button
                  type="button"
                  onClick={() => setShowChart(false)}
                  className="text-red-600 font-bold px-2"
                >
                  Close
                </button>
              </div>
              <div className="h-64 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={abstractData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="inward" fill="#4ade80" name="Inward" />
                    <Bar dataKey="outward" fill="#f87171" name="Outward" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={abstractData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="value" stroke="#6366f1" name="Value" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
